import {ChangeDetectionStrategy, Component, effect, EventEmitter, inject, input, OnInit, Output, ViewChild} from '@angular/core';
import {GMDatePipe} from "../../common/date.pipe";
import {MatTableDataSource, MatTableModule} from "@angular/material/table";
import {MatIcon} from "@angular/material/icon";
import {MatIconButton} from "@angular/material/button";
import {MatPaginator, MatPaginatorModule, PageEvent} from "@angular/material/paginator";
import {MatSort, MatSortModule} from "@angular/material/sort";
import {PersonBadgeComponent} from "../../common/person-picker/person-badge/person-badge.component";
import {StatusBadgeComponent} from "../../common/status-badge/status-badge.component";
import {Contact} from '../../../models/contact';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {CS} from '../../../models/cs';
import {EmptyViewPipe} from '../../common/empty-view.pipe';
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {AssignPersonComponent} from '../../common/assign-person/assign-person.component';
import {MatDialog} from '@angular/material/dialog';
import {CSService} from '../../services/cs.service';
import {environment} from '../../../environments/environment';
import {findLabel, toFilter} from '../../utils';
import {MatTooltip} from '@angular/material/tooltip';

export type CSData = CS & {
  bairro?: string;
  contact?: Contact;
  loadingAssignee?: boolean;
  _address?: string;
  _assignee?: string;
  _subject?: string;
  _type?: string;
  _bairro?: string;
  _description?: string;
}

@Component({
  selector: 'app-cs-table',
  imports: [MatFormFieldModule, MatInputModule, MatTableModule,
    MatSortModule, MatPaginatorModule, MatIcon, MatIconButton,
    PersonBadgeComponent, StatusBadgeComponent, GMDatePipe,
    EmptyViewPipe, MatProgressSpinner, MatTooltip],
  templateUrl: './cs-table.component.html',
  standalone: true,
  styleUrl: './cs-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsTableComponent implements OnInit {
  displayedColumns: string[] = ['id', 'timestamp', '_type', '_subject', '_bairro', '_assignee', 'status', 'open'];
  dataSource: MatTableDataSource<CSData> = new MatTableDataSource([] as CSData[]);

  @ViewChild(MatPaginator) paginator?: MatPaginator;
  @ViewChild(MatSort) sort?: MatSort;

  @Output() openDetails: EventEmitter<string> = new EventEmitter();
  @Output() pageChange: EventEmitter<PageEvent> = new EventEmitter();

  values = input.required<CS[]>();
  toBeDeleted = input<string>();
  totalItems = input<number>(0);
  pageSize = input<number>(10);

  csService = inject(CSService);
  readonly dialog = inject(MatDialog);

  constructor() {
    effect(() => {
      this.update();
    });
    if (this.csService.csTypeList().length === 0) {
      this.csService.getAllCsInformation();
    }
  }

  update() {
    this.dataSource.data = this.values()
      .filter((value) => value.id !== this.toBeDeleted())
      .map((value) => this.createNewCS(value));
  }

  ngOnInit() {
    this.update();
    setTimeout(() => {
      this.dataSource.sort = this.sort ?? null;
      this.sort?.sort({id: 'timestamp', start: 'desc', disableClear: false});
    }, 0);
  }

  onPageChange(event: PageEvent) {
    this.pageChange.emit(event);
  }

  openDialog(cs: CSData): void {
    cs.loadingAssignee = true;
    const dialogRef = this.dialog.open(AssignPersonComponent, {
      width: '400px',
      height: '100px',
      data: {},
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result && result.assignee) {
        await this.csService.updateAssignee(cs.id, result.assignee);
      }
      cs.loadingAssignee = false;
    });
  }

  createNewCS(cs: CS): CSData {
    return {
      ...cs,
      contact: cs.contact,
      _address: toFilter(cs.address),
      _assignee: toFilter(cs.assigneeUser?.name),
      _subject: toFilter(cs.subject),
      _type: toFilter(cs.type),
      _bairro: toFilter(cs.bairro),
      _description: toFilter(cs.delta ? cs.delta.map((d: any) => d.insert).join(' ').replaceAll('\n',' ') : ''),
    };
  }

  protected readonly environment = environment;
  protected readonly window = window;
  protected readonly findLabel = findLabel;
}
